
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ClipboardCheck, ShieldCheck, Layers, FileCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ValueMetricsSection } from "@/components/services/specializations/ValueMetricsSection";
import { HiddenCapabilitiesSection } from "@/components/services/specializations/HiddenCapabilitiesSection";
import { PlatformValueSection } from "@/components/services/specializations/PlatformValueSection";

const ISO27001 = () => {
  // Value metrics data
  const valueMetrics = [
    {
      value: "70%",
      label: "Controls Coverage",
      description: "Average ISO 27001 controls covered by existing technology after optimization"
    },
    {
      value: "45%",
      label: "Time Savings",
      description: "Reduction in compliance documentation time with our automation approach"
    },
    {
      value: "60%",
      label: "Faster Certification",
      description: "Typical acceleration in ISO 27001 certification timeline using our methodology"
    },
    {
      value: "2.5x",
      label: "ROI",
      description: "Average return on investment from our ISO 27001 implementation approach"
    }
  ];

  // Hidden capabilities data
  const hiddenCapabilities = [
    {
      title: "Microsoft 365 Compliance Features",
      description: "Microsoft 365 includes built-in controls and compliance features that map directly to ISO 27001 requirements across multiple domains.",
      percentage: 65
    },
    {
      title: "Documentation Automation",
      description: "Your existing collaboration tools can be configured to automatically generate and maintain much of the required ISO 27001 documentation.",
      percentage: 50
    },
    {
      title: "Compliance Monitoring",
      description: "Security monitoring tools you already own can be configured to provide continuous ISO 27001 compliance monitoring and reporting.",
      percentage: 40
    },
    {
      title: "Risk Management Integration",
      description: "Your existing risk management processes and tools can be aligned with ISO 27001 requirements with minimal modifications.",
      percentage: 55
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="relative">
        {/* Hero Section */}
        <section className="relative min-h-[40vh] flex items-center overflow-hidden pt-20 md:pt-24" aria-labelledby="hero-heading">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50/50 to-white" />
          
          <div className="absolute inset-0">
            {/* Background blobs */}
            <div className="absolute -top-48 left-0 w-[800px] h-[800px] bg-gradient-to-br from-violet-200/20 to-purple-300/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob will-change-transform" />
            <div className="absolute -bottom-48 right-0 w-[800px] h-[800px] bg-gradient-to-br from-fuchsia-200/20 to-pink-300/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000 will-change-transform" />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-purple-200/20 to-violet-300/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000 will-change-transform" />
            
            {/* Pattern overlay */}
            <div 
              className="absolute inset-0 opacity-[0.015]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236B46C1' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-white/50 to-transparent" />
          </div>

          <div className="container mx-auto relative px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 id="hero-heading" className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight tracking-tight text-gray-900">
                ISO 27001{" "}
                <span className="bg-gradient-to-r from-purple-600 to-violet-500 bg-clip-text text-transparent">
                  Implementation
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed font-light">
                Achieve ISO 27001 certification using technology you already own. Our approach maximizes your existing investments while building a robust information security management system.
              </p>
            </div>
          </div>
        </section>

        {/* Value Metrics Section */}
        <ValueMetricsSection 
          title="Accelerate ISO 27001 Certification"
          subtitle="Business Impact"
          description="Our ISO 27001 implementation approach delivers faster certification while leveraging your existing technology investments."
          metrics={valueMetrics}
        />

        {/* Hidden Capabilities Section */}
        <HiddenCapabilitiesSection 
          platform="Your Existing Infrastructure"
          capabilities={hiddenCapabilities}
        />

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Service Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Assessment Card */}
              <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <ClipboardCheck className="h-12 w-12 text-purple-600 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-semibold mb-3">ISO 27001 Readiness Assessment</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive evaluation of your current security posture against ISO 27001, identifying gaps and existing coverage.
                </p>
                <Button variant="outline" className="w-full group">
                  Learn More 
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </div>

              {/* Implementation Card */}
              <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <ShieldCheck className="h-12 w-12 text-purple-600 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-semibold mb-3">Implementation Support</h3>
                <p className="text-gray-600 mb-4">
                  Expert guidance on implementing ISO 27001 controls using your existing technology stack, maximizing current investments.
                </p>
                <Button variant="outline" className="w-full group">
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </div>

              {/* Certification Card */}
              <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <FileCheck className="h-12 w-12 text-purple-600 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-semibold mb-3">Certification Preparation</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive audit preparation support, including documentation, evidence collection, and staff training.
                </p>
                <Button variant="outline" className="w-full group">
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>

          {/* ISO 27001 Domains Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
                ISO 27001 Control Domains
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Our approach helps you implement all ISO 27001 domains while maximizing the value of your existing security tools.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {/* ISO 27001 Domains - First Row */}
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Information Security Policies</h3>
                <p className="text-gray-600">
                  We help you develop robust security policies using templates and automation tools you already own.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Organization of Information Security</h3>
                <p className="text-gray-600">
                  We implement organizational structures and processes using your existing collaboration platforms.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Human Resource Security</h3>
                <p className="text-gray-600">
                  We configure your HR and identity management systems to satisfy ISO 27001 personnel security requirements.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Asset Management</h3>
                <p className="text-gray-600">
                  We leverage your existing inventory systems and endpoint management tools for ISO-compliant asset management.
                </p>
              </div>

              {/* ISO 27001 Domains - Second Row */}
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Access Control</h3>
                <p className="text-gray-600">
                  We optimize your identity and access management systems to implement ISO 27001 access control requirements.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Cryptography</h3>
                <p className="text-gray-600">
                  We help you implement cryptographic controls using native platform capabilities you already have.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Operations Security</h3>
                <p className="text-gray-600">
                  We optimize your IT operations and security monitoring tools to satisfy ISO operational security requirements.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Communications Security</h3>
                <p className="text-gray-600">
                  We implement network security controls using your existing networking and communication platforms.
                </p>
              </div>
            </div>
          </div>

          {/* Platform Value Section */}
          <PlatformValueSection
            title="Achieve ISO 27001 with Tools You Already Own"
            subtitle="Value Maximization"
            description="Our approach helps you implement ISO 27001 by optimizing the security and compliance tools you already own, avoiding unnecessary technology investments."
            benefits={[
              {
                title: "Microsoft 365 Compliance Integration",
                description: "Map Microsoft 365 compliance features directly to ISO 27001 controls, maximizing your existing license value."
              },
              {
                title: "Documentation Automation",
                description: "Create ISO-required documentation using your existing collaboration and content management systems."
              },
              {
                title: "Control Implementation",
                description: "Configure your current security tools to satisfy ISO requirements without purchasing new solutions."
              },
              {
                title: "ISMS Management",
                description: "Manage your Information Security Management System using existing project management and governance tools."
              }
            ]}
            imageSrc="/lovable-uploads/946673b9-aa6f-4e0b-af68-52257c4d1af5.png"
            imageAlt="ISO 27001 implementation using existing tools"
            linkUrl="/get-started"
            linkText="Schedule an ISO 27001 Assessment"
          />

          {/* Case Studies Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12 mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl text-center mb-12">
                Success Stories
              </h2>
              
              <div className="grid grid-cols-1 gap-8">
                {/* SaaS Company Case Study */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="md:grid md:grid-cols-3 items-center">
                    <div className="hidden md:block relative h-64 md:h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/30 to-blue-100/30 mix-blend-multiply" />
                      <img
                        src="/lovable-uploads/1255917e-e514-4e0b-9b3c-0dd1b61d415c.png"
                        alt="SaaS company implementing ISO 27001"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20" />
                    </div>
                    <div className="col-span-2 p-8 space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">B2B SaaS Provider</h3>
                          <p className="text-purple-600 font-medium">120+ Employees</p>
                        </div>
                        <div className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                          50% Faster Certification
                        </div>
                      </div>
                      <p className="text-gray-600">
                        Achieved ISO 27001 certification in just 6 months using their existing Microsoft 365 E5 suite and security tools, while competitors took 12+ months with new system implementations.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Used SharePoint for all ISO-required documentation and records</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Implemented 75% of required controls using existing Microsoft security features</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Automated compliance monitoring with existing security monitoring tools</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Manufacturing Company Case Study */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="md:grid md:grid-cols-3 items-center">
                    <div className="hidden md:block relative h-64 md:h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/30 to-blue-100/30 mix-blend-multiply" />
                      <img
                        src="/lovable-uploads/739faa3f-9942-4127-bffa-21edf1ab7894.png"
                        alt="Manufacturing company implementing ISO 27001"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20" />
                    </div>
                    <div className="col-span-2 p-8 space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">Mid-Size Manufacturer</h3>
                          <p className="text-purple-600 font-medium">350+ Employees</p>
                        </div>
                        <div className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                          40% Cost Savings
                        </div>
                      </div>
                      <p className="text-gray-600">
                        Implemented ISO 27001 while saving 40% compared to competitors by optimizing their existing technology stack rather than purchasing specialized compliance tools.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Reconfigured existing SIEM system for ISO 27001 compliance monitoring</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Integrated Microsoft Defender with third-party manufacturing systems</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Used Power BI to create compliance dashboards from existing data sources</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-purple-600 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Achieve ISO 27001 with Your Existing Tools?
              </h2>
              <p className="text-purple-100 mb-8 max-w-2xl mx-auto">
                Schedule a consultation to discover how we can help you implement ISO 27001 while maximizing your existing technology investments.
              </p>
              <Link to="/get-started">
                <Button 
                  className="bg-white text-purple-600 hover:bg-purple-50 transition-colors duration-300"
                  size="lg"
                >
                  Schedule a Consultation
                </Button>
              </Link>
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-white to-purple-50/30">
              <div className="absolute inset-0 bg-white/40 backdrop-blur-3xl" />
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-purple-100/30 to-transparent" />
              
              <div className="relative py-20 px-8 md:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div className="space-y-10">
                    <div className="space-y-6">
                      <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        The edgefi ISO 27001 Advantage
                      </h2>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        Our unique approach helps organizations achieve ISO 27001 certification faster and more cost-effectively by leveraging technology they already own.
                      </p>
                    </div>

                    <div className="space-y-8 relative">
                      <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-purple-400 to-purple-100 rounded-full" aria-hidden="true" />
                      <div className="pl-8">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                          Our Process
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          We follow a structured, proven approach to ISO 27001 implementation:
                        </p>
                        <ul className="mt-6 space-y-5">
                          <li className="flex items-start gap-4">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <ClipboardCheck className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            </div>
                            <span className="text-gray-600 leading-relaxed mt-1">Comprehensive gap analysis against ISO 27001 requirements</span>
                          </li>
                          <li className="flex items-start gap-4">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <Layers className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            </div>
                            <span className="text-gray-600 leading-relaxed mt-1">Mapping of existing tools to ISO 27001 control objectives</span>
                          </li>
                          <li className="flex items-start gap-4">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <FileCheck className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            </div>
                            <span className="text-gray-600 leading-relaxed mt-1">Implementation of an ISMS that integrates with your current systems</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <Link 
                      to="/resources/how-to"
                      className="group inline-flex items-center gap-3 px-6 py-3 bg-purple-50 hover:bg-purple-100 text-purple-600 font-semibold rounded-full transition-colors duration-300"
                    >
                      Explore Our Resources
                      <ArrowRight 
                        className="h-5 w-5 transition-transform duration-300 transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </Link>
                  </div>

                  <div className="relative">
                    <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-10 border border-purple-100/50 shadow-[0_4px_20px_-4px_rgba(107,70,193,0.15)] transition-all duration-500 hover:shadow-[0_8px_30px_-4px_rgba(107,70,193,0.25)]">
                      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-100/30 to-blue-100/30 rounded-full blur-3xl transform -translate-x-20 -translate-y-20" aria-hidden="true" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-100/30 to-purple-100/30 rounded-full blur-3xl transform translate-x-10 translate-y-10" aria-hidden="true" />
                      
                      <div className="relative">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-8">
                          Benefits of Our Approach
                        </h3>
                        <div className="space-y-5">
                          <div className="p-6 bg-gradient-to-br from-purple-50/70 to-white rounded-xl border border-purple-100/20">
                            <h4 className="font-medium text-gray-900">Cost Efficiency</h4>
                            <p className="text-gray-600 mt-2">Maximize existing security investments instead of buying new tools</p>
                          </div>
                          <div className="p-6 bg-gradient-to-br from-blue-50/70 to-white rounded-xl border border-blue-100/20">
                            <h4 className="font-medium text-gray-900">Faster Certification</h4>
                            <p className="text-gray-600 mt-2">Achieve compliance more quickly by leveraging familiar tools</p>
                          </div>
                          <div className="p-6 bg-gradient-to-br from-green-50/70 to-white rounded-xl border border-green-100/20">
                            <h4 className="font-medium text-gray-900">Sustainable Compliance</h4>
                            <p className="text-gray-600 mt-2">Build compliance into your existing processes for long-term success</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ISO27001;
